import React, { useState } from "react";
import "./Footer.css";
import footerBackdrop from "../images/Footer_rectangle.png";
import newsletterCard from "../images/HomePage_eventCard.png";
import footerLogo from "../images/Footer_logo.png";
import facebookIcon from "../images/HomePage_facebook.png";
import instagramIcon from "../images/HomePage_instagram.png";
import tiktokIcon from "../images/HomePage_tiktok.png";
import linkedinIcon from "../images/HomePage_linkedin.png";
import youtubeIcon from "../images/HomePage_youtube.png";

const socialLinks = [
    {
        name: "Facebook",
        href: "https://www.facebook.com/culturedkidscuisine/",
        icon: facebookIcon,
    },
    {
        name: "Instagram",
        href: "https://www.instagram.com/culturedkidscuisine/?hl=en",
        icon: instagramIcon,
    },
    {
        name: "TikTok",
        href: "https://www.tiktok.com/@culturedkidscuisine?_d=secCgsIARCbDRgBIAIoARI%2BCjxiVrsEgPhYrJN3oC7ZD6o9nncyiOpmmIO%2BKn3ZRSr38cuztaQgI6AKsqO1JuQ9vKv3n4rJFe1u8Vz%2BRC0aAA%3D%3D&language=en&sec_uid=MS4wLjABAAAA3fXWAG-ZIVx0c2I4ebvv5nmpLWdWTSStR8uB4deTUhvS9iUguFo8fc",
        icon: tiktokIcon,
    },
    {
        name: "LinkedIn",
        href: "https://www.linkedin.com/company/cultured-kids-cuisine/",
        icon: linkedinIcon,
    },
    {
        name: "YouTube",
        href: "https://www.youtube.com/channel/UCyMuT8ywBkYrwxWX_1dK7Gw",
        icon: youtubeIcon,
    },
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const getApiUrl = (path) => `${API_BASE_URL}${path}`;

const parseJsonResponse = async (response) => {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
        return response.json();
    }

    throw new Error("The backend API did not return JSON. Make sure the backend is running and the frontend proxy is configured.");
};

const Footer = () => {
    const [newsletterEmail, setNewsletterEmail] = useState("");
    const [newsletterStatus, setNewsletterStatus] = useState({ type: "", message: "" });
    const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
    const [contactForm, setContactForm] = useState({
        fullName: "",
        email: "",
        message: "",
    });
    const [contactStatus, setContactStatus] = useState({ type: "", message: "" });
    const [contactSubmitting, setContactSubmitting] = useState(false);

    const submitNewsletter = async (event) => {
        event.preventDefault();

        const email = newsletterEmail.trim();

        if (!emailPattern.test(email)) {
            setNewsletterStatus({ type: "error", message: "Please enter a valid email address." });
            return;
        }

        setNewsletterSubmitting(true);
        setNewsletterStatus({ type: "", message: "" });

        try {
            const response = await fetch(getApiUrl("/api/footer/newsletter"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            const data = await parseJsonResponse(response);

            if (!response.ok) {
                throw new Error(data.error || "Newsletter signup failed.");
            }

            setNewsletterEmail("");
            setNewsletterStatus({ type: "success", message: "You're on the list!" });
        } catch (error) {
            setNewsletterStatus({ type: "error", message: error.message });
        } finally {
            setNewsletterSubmitting(false);
        }
    };

    const updateContactField = (event) => {
        const { name, value } = event.target;
        setContactForm((currentForm) => ({
            ...currentForm,
            [name]: value,
        }));
    };

    const submitContact = async (event) => {
        event.preventDefault();

        const fullName = contactForm.fullName.trim();
        const email = contactForm.email.trim();
        const message = contactForm.message.trim();

        if (!fullName || !emailPattern.test(email) || !message) {
            setContactStatus({ type: "error", message: "Please complete every field with a valid email." });
            return;
        }

        setContactSubmitting(true);
        setContactStatus({ type: "", message: "" });

        try {
            const response = await fetch(getApiUrl("/api/footer/contact"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ fullName, email, message }),
            });
            const data = await parseJsonResponse(response);

            if (!response.ok) {
                throw new Error(data.error || "Contact form submission failed.");
            }

            setContactForm({ fullName: "", email: "", message: "" });
            setContactStatus({ type: "success", message: "Thanks! We received your message." });
        } catch (error) {
            setContactStatus({ type: "error", message: error.message });
        } finally {
            setContactSubmitting(false);
        }
    };

    return (
        <footer className="site-footer" style={{ backgroundImage: `url(${footerBackdrop})` }}>
            <div className="footer-content">
                <section className="footer-newsletter-card" style={{ backgroundImage: `url(${newsletterCard})` }}>
                    <div className="footer-newsletter-inner">
                        <h2>Subscribe to our Newsletter</h2>
                        <p>
                            Want to know everything before everyone else? Join our mailing list to
                            receive updates & news on our latest products, events and workshops.
                        </p>

                        <form className="footer-newsletter-form" onSubmit={submitNewsletter}>
                            <label className="footer-visually-hidden" htmlFor="footer-newsletter-email">
                                Email address
                            </label>
                            <input
                                id="footer-newsletter-email"
                                type="email"
                                value={newsletterEmail}
                                onChange={(event) => setNewsletterEmail(event.target.value)}
                                placeholder="example@emailaddress.com"
                                disabled={newsletterSubmitting}
                            />
                            <span className="footer-adult-note">
                                By entering your email address, you are confirming that you are an adult.
                            </span>
                            <button type="submit" disabled={newsletterSubmitting}>
                                {newsletterSubmitting ? "SIGNING UP..." : "SIGN UP NOW"}
                            </button>
                            {newsletterStatus.message && (
                                <div className={`footer-form-status ${newsletterStatus.type}`}>
                                    {newsletterStatus.message}
                                </div>
                            )}
                        </form>

                        <div className="footer-social-area">
                            <h3>Follow us on Social Media</h3>
                            <div className="footer-social-links">
                                {socialLinks.map((socialLink) => (
                                    <a
                                        key={socialLink.name}
                                        href={socialLink.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Visit Cultured Kids Cuisine on ${socialLink.name}`}
                                    >
                                        <img src={socialLink.icon} alt="" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="footer-contact-area" aria-labelledby="footer-contact-title">
                    <h2 id="footer-contact-title">Contact us</h2>
                    <form className="footer-contact-form" onSubmit={submitContact}>
                        <div className="footer-contact-row">
                            <label className="footer-visually-hidden" htmlFor="footer-full-name">
                                Full name
                            </label>
                            <input
                                id="footer-full-name"
                                name="fullName"
                                type="text"
                                value={contactForm.fullName}
                                onChange={updateContactField}
                                placeholder="Full name"
                                disabled={contactSubmitting}
                            />

                            <label className="footer-visually-hidden" htmlFor="footer-contact-email">
                                Email address
                            </label>
                            <input
                                id="footer-contact-email"
                                name="email"
                                type="email"
                                value={contactForm.email}
                                onChange={updateContactField}
                                placeholder="Email address"
                                disabled={contactSubmitting}
                            />
                        </div>

                        <label className="footer-visually-hidden" htmlFor="footer-message">
                            Message
                        </label>
                        <textarea
                            id="footer-message"
                            name="message"
                            value={contactForm.message}
                            onChange={updateContactField}
                            placeholder="Message"
                            disabled={contactSubmitting}
                        />

                        <div className="footer-contact-actions">
                            {contactStatus.message && (
                                <div className={`footer-form-status ${contactStatus.type}`}>
                                    {contactStatus.message}
                                </div>
                            )}
                            <button type="submit" disabled={contactSubmitting}>
                                {contactSubmitting ? "SENDING..." : "SUBMIT"}
                            </button>
                        </div>
                    </form>
                </section>
            </div>

            <div className="footer-bottom">
                <div className="footer-brand">
                    <img src={footerLogo} alt="Cultured Kids Cuisine logo" />
                    <span>Cultured Kids Cuisine</span>
                </div>
                <p>&copy; Cultured Kids Cuisine 2020-2026. All Rights Reserved.</p>
            </div>
        </footer>
    );
};

export default Footer;
